import { isDefined, isNonEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { useGridPatchedCells } from 'components/common-form/hooks/patched-fields/useGridPatchedCells';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { updateArray } from 'components/common-form/utils/RowUtils';
import { IconWithPopover } from 'components/shared/ErrorsPanel/Details/Icon/IconWithPopover';
import { FormattedMessage } from 'components/shared/Intl';
import type { ErrorDetailsWithoutSummary } from 'contexts/errorMessages/ErrorMessages.context';
import { ErrorMessagesContext } from 'contexts/errorMessages/ErrorMessages.context';
import type { ProductCodesGridMetadata } from 'models/forms/fields';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { FC, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import styled from 'styled-components';
import type { IWorkflowError } from 'utils/errors/workflowErrors';

import type { ProductCodeData } from './ProductCodeData';
import type { ProductCodesProps } from './ProductCodes.connect';
import { connectProductCodes } from './ProductCodes.connect';
import { ProductCodesRow } from './Row';

const GetPreTradeCodesContainer = styled.div`
  position: absolute;
  right: var(--spacing-1);
  top: var(--spacing-1);
`;

const ErrorIcon: FC<{
  errors: readonly NextWorkflowError[];
  className?: string;
}> = ({ errors, className }) => {
  const errorMessagesContext: ErrorDetailsWithoutSummary = {
    hasSummaryFromErrorMessages: false,
    errorMessages: errors.map(mapToLegacyError),
    shouldUseRejectionCode: true,
  };

  return (
    <ErrorMessagesContext.Provider value={errorMessagesContext}>
      <IconWithPopover
        id="product-codes-error-icon"
        e2e="product-codes-error-icon"
        placement="top"
        className={className}
      />
    </ErrorMessagesContext.Provider>
  );
};

const ProductCodesGridRaw: FC<ProductCodesProps> = ({
  onGenerateCodes,
  isGenerateAllProductCodesReadonly,
  isGenerateAllProductCodesVisible,
  errors,
  node: { id: fieldId },
  tabIndex,
}) => {
  const { formatMessage } = useIntl();
  const metadata = useFieldMetadata<ProductCodesGridMetadata>(fieldId)!;
  const [gridData, setGridData] = useData<ProductCodeData[]>(fieldId, []);
  const getCellHighlight = useGridPatchedCells(fieldId);

  const onCellChange = useCallback(
    (code: string, column: keyof ProductCodeData, value: unknown) => {
      const updated = updateArray<ProductCodeData>(
        gridData,
        r => r.ProductCodeKey === code,
        previous => ({ ...previous, [column]: value }),
      );
      setGridData(updated);
    },
    [gridData, setGridData],
  );

  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);

  const hasError = isNonEmpty(errors);

  return (
    <table className="table table-sm table-borderless" data-e2e="editable-grid-ProductCodes">
      <thead>
        <tr>
          <th>
            <FormattedMessage id="form.ProductCodeKey" />
          </th>
          {!isGenerateAllProductCodesVisible && (
            <th>
              <FormattedMessage id="form.CodeKey" />
            </th>
          )}
          {isGenerateAllProductCodesVisible && (
            <th className="position-relative">
              <FormattedMessage id="form.CodeKey" />
              <GetPreTradeCodesContainer>
                <button
                  className="btn btn-icon-end btn-flat-secondary px-1 py-0 position-relative h-100 d-flex justify-content-center align-items-center"
                  data-e2e="GetPreTradeCodes"
                  disabled={isGenerateAllProductCodesReadonly}
                  onClick={() => {
                    logAnalytics.trackEvent(logAnalyticsCategory, 'Get pre-trade codes');
                    onGenerateCodes();
                  }}
                  tabIndex={tabIndex}
                >
                  <span className={classnames({ 'text-danger': hasError })}>
                    {formatMessage({ id: 'form.GetPreTradeCodes' })}
                  </span>
                  <i className={classnames('icon', { 'text-danger': hasError })}>cloud_download</i>
                </button>
                {hasError && <ErrorIcon errors={errors!} className="ms-2 pt-0" />}
              </GetPreTradeCodesContainer>
            </th>
          )}
          <th className="text-center">{formatMessage({ id: 'form.ProductCodeIsRequested' })}</th>
          {metadata.isDisplayTermsheetColumnVisible && (
            <th className="text-center">{formatMessage({ id: 'form.ProductCodeDisplayInTS' })}</th>
          )}
        </tr>
      </thead>
      <tbody>
        {metadata.rows
          .map((rowMetadata, rowIndex) => {
            const rowData = gridData?.find(r => r.ProductCodeKey === rowMetadata.key);
            return rowData ? (
              <ProductCodesRow
                code={rowData.ProductCodeKey}
                displayInTermsheet={rowData.ProductCodeDisplayInTS}
                isDisplayInTermsheetReadonly={rowMetadata.isRequestedReadonly}
                isDisplayInTermsheetVisible={metadata.isDisplayTermsheetColumnVisible}
                isRequested={rowData.ProductCodeIsRequested}
                isRequestedReadonly={rowMetadata.isRequestedReadonly}
                isValueReadonly={rowMetadata.isValueReadonly}
                key={rowMetadata.key}
                rowIndex={rowIndex}
                getCellHighlight={getCellHighlight}
                onCellChange={onCellChange}
                value={rowData.ProductCodeValue}
              />
            ) : undefined;
          })
          .filter(isDefined)}
      </tbody>
    </table>
  );
};
ProductCodesGridRaw.displayName = 'ProductCodesGridRaw';

export const ProductCodesGrid = connectProductCodes(ProductCodesGridRaw);

ProductCodesGrid.displayName = 'ProductCodesGrid';

export function mapToLegacyError(error: NextWorkflowError): IWorkflowError {
  return {
    RejectionType: error.rejectionType,
    RejectionCode: error.rejectionCode,
    RejectionOrigin: error.rejectionOrigin,
    RejectionMessage: error.rejectionMessage,
    RejectionMessageCode: error.rejectionMessageCode,
    OriginMessage: error.originMessage,
    OutOfRangeLowLimit: error.outOfRangeLowLimit,
    OutOfRangeHighLimit: error.outOfRangeHighLimit,
    OutOfRangeCondition: error.outOfRangeCondition,
  };
}
